import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonModal, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { BerService } from '../ber.service';
import { NaploService } from '../../naplo/naplo.service';
import { DataService } from '../../data.service';
import { KeretService } from '../../keret.service';
import { BER_OPCIOK, adozasiForma } from '../ber.opciok';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';

// BER ügyfelek (foglalkoztatók). A NAPLO ügyféltörzséből importálhatók, és felhasználóbarát
// megjelenítési név adható (fallback: a cég hivatalos neve).
@Component({
  selector: 'app-ber-ugyfelek',
  templateUrl: './ugyfelek.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss'],
})
export class UgyfelekComponent implements OnInit, OnDestroy {
  @ViewChild('ugyfelModal') ugyfelModal: IonModal;
  @ViewChild('importModal') importModal: IonModal;
  intezmeny = '';
  ugyfelek: any[] = [];
  szerk: any = null;
  evek: number[] = [];
  honapok = BER_OPCIOK.honapok;
  khanyadok = BER_OPCIOK.koltseghanyad;
  naploUgyfelek: any[] = [];
  importValasztott: Record<string, boolean> = {};

  // Szűrők: név/azonosító, adószám és adózási forma. Kliens-oldali.
  szuroNev = '';
  szuroAdoszam = '';
  szuroForma = ''; // '' = mind | kata | atalany | egyeb

  get szurtUgyfelek(): any[] {
    const nev = this.szuroNev.trim().toLowerCase();
    const ado = this.szuroAdoszam.replace(/\D/g, '');
    const forma = this.szuroForma;
    return (this.ugyfelek || []).filter((u) => {
      if (nev) {
        const talalat = `${u.megjelenites_nev || ''} ${u.nev || ''} ${u.azonosito || ''}`.toLowerCase().includes(nev);
        if (!talalat) return false;
      }
      if (ado && !String(u.adoszam || '').replace(/\D/g, '').includes(ado)) return false;
      if (forma && adozasiForma(u) !== forma) return false;
      return true;
    });
  }

  szuroTorol() { this.szuroNev = ''; this.szuroAdoszam = ''; this.szuroForma = ''; }

  constructor(private ber: BerService, private router: Router, private toast: ToastController, private alert: AlertController,
              private naplo: NaploService, private data: DataService, private keret: KeretService,
              private szerkZar: SzerkZarService) {}

  /** A „Könyvelés" gomb csak annak, akinek ennél az intézménynél van NAPLO-modulja. */
  naploElerheto = false;

  async ngOnInit() {
    const a = await this.ber.init();
    this.intezmeny = a.intezmeny;
    try {
      const modok = (await this.data.getFromStorage('sajatIntezmeny'))?.mode;
      this.naploElerheto = Array.isArray(modok) && modok.includes('NAPLO');
    } catch { /* jog nélkül a gomb rejtve marad */ }
    const ev = new Date().getFullYear();
    this.evek = [ev + 1, ev, ev - 1, ev - 2, ev - 3, ev - 4];
    await this.betolt();
  }

  /*  ÁTVÁLTÁS A KÖNYVELÉSRE – a Könyvelés ügyfélkártyáján lévő „Bérszámfejtés" gomb párja.
   *
   *  Nem elég navigálni: az ALRENDSZERT is át kell állítani (KeretService → modValt), különben a
   *  könyvelési képernyő bér-kerettel nyílna meg – bal menü, fejléc és jogosultság-lekérés
   *  mind a Bérszámfejtésé maradna.
   *
   *  Ha a foglalkoztató még nincs könyvelési ügyfélhez kötve, NEM csinálunk csendben újat: a
   *  meglévő „Cég átvétele a bérből" importot ajánljuk fel (az adószám alapján a már meglévő
   *  könyvelési ügyfelet köti össze duplikálás helyett), és csak utána váltunk. */
  async atvaltKonyveles(u: any) {
    let n = await this.konyvelesiUgyfel(u);
    if (n === 'nincs-kotes') {
      if (!(await this.megerosit(`${this.nevMegjelenit(u)} még nincs összekötve könyvelési ügyféllel. Átveszem a könyvelésbe?`
        + ' Ha az adószáma alapján már van könyvelési ügyfél, azt kötöm hozzá; ha nincs, új készül'
        + ' egyszeres könyveléssel – ez a Könyvelés ügyfélkártyáján átállítható.'))) return;
      try { await this.naplo.post('/ugyfel/import-ber', { intezmeny: this.intezmeny, ber_ugyfel_idk: [u.id] }); }
      catch (e: any) { return this.hiba(e?.error?.error || 'Az átvétel nem sikerült – a könyvelés Ügyfelek képernyőjének joga kell hozzá.'); }
      await this.betolt();                       // a kártyán megjelenjen a NAPLO-címke
      n = await this.konyvelesiUgyfel(u);
    }
    if (n === 'nincs-kotes') return this.hiba('Az átvétel után sem találom a könyvelési ügyfelet – nézd meg a Könyvelés → Ügyfelek képernyőn.');
    if (!n) return;                              // a hibaüzenetet a konyvelesiUgyfel adta
    await this.naplo.init();
    await this.naplo.setAktiv({ ugyfelId: n.id, ugyfelNev: n.nev, konyveles_mod: n.konyveles_mod || 'egyszeres', tipus: n.tipus || 'ceg',
      bank_naplo: !!n.bank_naplo });
    const k = this.keret.a;
    const mod = (k?.modok() || []).find((m) => m.name === 'NAPLO');
    if (k && mod) k.modValt(mod, '/naplo/fomenu');
    else this.router.navigateByUrl('/naplo/fomenu');   // keret nélkül (fixált NAPLO-app): a régi út
  }

  /** A foglalkoztatóhoz kötött könyvelési ügyfél; 'nincs-kotes', ha még nincs; null, ha hiba volt. */
  private async konyvelesiUgyfel(u: any): Promise<any | 'nincs-kotes' | null> {
    try { return await this.naplo.get('/ugyfel/by-ber/' + u.id); }
    catch (e: any) {
      if (e?.error?.nincsKotes) return 'nincs-kotes';
      this.hiba(e?.error?.error || 'Nem sikerült átváltani a könyvelésre.');
      return null;
    }
  }

  private async megerosit(uzenet: string): Promise<boolean> {
    const a = await this.alert.create({ header: 'Átvétel a könyvelésbe', message: uzenet,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Átveszem', role: 'confirm' }] });
    await a.present();
    return (await a.onDidDismiss()).role === 'confirm';
  }

  get aktiv() { return this.ber.aktiv; }
  adozasiForma = adozasiForma;
  nevMegjelenit(u: any) { return u.megjelenites_nev || u.nev; }

  async betolt() {
    //  A kezelőoldal mindig friss listát tölt (a közös, rövid ideig tárolt listát üríti – ber.service).
    this.ber.ugyfelListaUrit();
    try { this.ugyfelek = await this.ber.get('/ugyfelek/' + this.intezmeny); }
    catch { this.hiba('Nem sikerült betölteni az ügyfeleket.'); }
  }

  async valaszt(u: any) {
    await this.ber.setAktiv({ ugyfelId: u.id, ugyfelNev: this.nevMegjelenit(u), kivas: !!u.kivas, adozas: adozasiForma(u), atalanyado: !!u.atalanyado, sulinform: u.sulinform_feladatellatas || u.kapcsolat_id || null });
    this.ok(this.nevMegjelenit(u) + ' kiválasztva.');
  }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); this.ok('Bérszámfejtési év: ' + ev); }
  async hoValt(ho: number) { await this.ber.setAktiv({ ho }); }

  ugyfelkorNyitva = false;
  /*  Kapcsolt módban a Könyvelés még bér-ügyfél nélküli ügyfele „virtuális” sor: az első választáskor
   *  jön létre a bér-ügyfele, összekötve (backend/ber.js POST /ugyfel/naplobol). */
  async belep(u: any) {
    if (u.virtualis) {
      try {
        const r: any = await this.ber.post('/ugyfel/naplobol/' + u.naplo_ugyfel_id, {});
        await this.betolt();
        u = this.ugyfelek.find((x) => x.id === r.id) || u;
      } catch (e: any) { (await this.toast.create({ message: e?.error?.error || 'Nem sikerült létrehozni a bér-ügyfelet.', duration: 2800, color: 'danger' })).present(); return; }
    }
    await this.valaszt(u);
    this.router.navigateByUrl('/ber/fomenu');
  }

  /** A telepítés SULINFORM-intézményei (a jelenlét-átvételhez köthető). */
  feladatellatasok: { id: string; name: string }[] = [];
  /** A kapcsolati kóddal beállított forrás a listában is látszódjon (és az aktív ügyfélnél a SULINFORM-gombok). */
  async kapcsolatValtozott(k: any) {
    const u = this.ugyfelek.find((x) => x.id === this.szerk?.id);
    if (u) u.kapcsolat_id = k?.id || null;
    if (this.szerk) this.szerk.kapcsolat_id = k?.id || null;
    if (u && this.ber.aktiv?.ugyfelId === u.id) await this.ber.setAktiv({ sulinform: u.sulinform_feladatellatas || u.kapcsolat_id || null });
  }
  /** Iktató-kapcsolat: hova iktasson a rendszer, ha a Bérből iktatunk. */
  iktatok: any[] = [];
  iktatoKonyvekMind: any[] = [];
  get iktatoKonyvek() {
    const i = this.szerk?.iktato_intezmeny;
    return this.iktatoKonyvekMind.filter((k) => !i || k.intezmeny === i);
  }
  private async iktatokBetolt() {
    if (this.iktatok.length) return;
    try { const r: any = await this.ber.get('/iktatok'); this.iktatok = r.iktatok || []; this.iktatoKonyvekMind = r.konyvek || []; }
    catch { this.iktatok = []; this.iktatoKonyvekMind = []; }
  }
  private async feladatellatasBetolt() {
    if (this.feladatellatasok.length) return;
    try { this.feladatellatasok = await this.ber.sulinformApi.get('/feladatellatasok'); } catch { this.feladatellatasok = []; }
  }

  /*  SZERKESZTÉSI ZÁR (components/szerk-zar): a meglévő ügyfelet egyszerre egy ember szerkesztheti.
   *  A sorszám a késve érkező foglalást dobja el, ha a szerkesztőt közben bezárták. */
  zarUgyfel: SzerkZar | null = null;
  private zarSorsz = 0;
  private async zarFoglal(id: string) {
    const s = ++this.zarSorsz;
    this.zarUgyfel?.felenged(); this.zarUgyfel = null;
    const z = await this.szerkZar.foglal('ber', 'ber_ugyfel', id, { ujratolt: () => this.ugyfelUjratolt(id) });
    if (s !== this.zarSorsz) { z?.felenged(); return; }
    this.zarUgyfel = z;
  }
  zarEngedi() { this.zarSorsz++; this.zarUgyfel?.felenged(); this.zarUgyfel = null; }
  /** A zár felszabadulásakor / újratöltéskor: friss adat a nyitott szerkesztőbe. */
  private async ugyfelUjratolt(id: string) {
    await this.betolt();
    const u = this.ugyfelek.find((x) => x.id === id);
    if (u && this.szerk?.id === id) this.szerk = this.szerkbol(u);
  }
  ngOnDestroy() { this.zarEngedi(); }

  uj() {
    this.zarEngedi();
    this.feladatellatasBetolt();
    this.iktatokBetolt();
    this.naploBetolt();
    this.szerk = { intezmeny: this.intezmeny, azonosito: '', nev: '', megjelenites_nev: '', atalanyado: false, kata: false, kifizetohelyi: false, kivas: false, atalany_koltseghanyad: 45, inaktiv: false };
    this.ugyfelModal.present();
  }
  szerkeszt(u: any) {
    this.feladatellatasBetolt();
    this.iktatokBetolt();
    this.naploBetolt();
    this.szerk = this.szerkbol(u);
    this.ugyfelModal.present();
    this.zarFoglal(u.id);
  }
  private szerkbol(u: any) {
    return { ...u, naplo_ugyfel_id: u.naplo_ugyfel_id || null, iktato_intezmeny: u.iktato_intezmeny || '', iktato_konyv: u.iktato_konyv || '', atalanyado: !!u.atalanyado, kata: !!u.kata, kifizetohelyi: !!u.kifizetohelyi, kivas: !!u.kivas, inaktiv: !!u.inaktiv, atalany_koltseghanyad: Number(u.atalany_koltseghanyad) || 45, intezmeny: this.intezmeny, sulinform: u.sulinform_feladatellatas || '' };
  }

  async mentes() {
    if (!this.szerk?.nev?.trim()) return this.hiba('A név kötelező.');
    if (this.zarUgyfel && !this.zarUgyfel.irhato) return;
    this.szerk.intezmeny = this.intezmeny;
    try {
      let id = this.szerk.id;
      if (id) await this.ber.put('/ugyfel/' + id, this.szerk);
      else id = (await this.ber.post<any>('/ugyfel', this.szerk))?.id;
      // A SULINFORM-kötés külön végponton (ber-sulinform.js) – csak ha változott.
      const regi = this.ugyfelek.find((x) => x.id === id)?.sulinform_feladatellatas || '';
      if (id && (this.szerk.sulinform || '') !== regi) await this.ber.sulinformApi.put('/ugyfel/' + id, { feladatellatas: this.szerk.sulinform || null });
      await this.ugyfelModal.dismiss();
      await this.betolt();
      await this.ber.aktivUgyfelFrissit();
      this.ok('Mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }

  async torol(u: any) {
    const a = await this.alert.create({
      header: 'Törlés', message: `Biztosan törli: ${this.nevMegjelenit(u)}?`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, {
        text: 'Törlés', role: 'destructive', handler: async () => {
          try { await this.ber.del('/ugyfel/' + u.id); await this.betolt(); this.ok('Törölve.'); }
          catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
        }
      }],
    });
    a.present();
  }

  /** A könyvelési ügyfelek listája az összekötéshez (a szerkesztő legördülőjébe). */
  async naploBetolt() {
    if (this.naploUgyfelek.length) return;
    try { this.naploUgyfelek = await this.ber.get('/naplo-ugyfelek/' + this.intezmeny); }
    catch { this.naploUgyfelek = []; }
  }

  // --- NAPLO import ---
  async importNyit() {
    this.importValasztott = {};
    try { this.naploUgyfelek = await this.ber.get('/naplo-ugyfelek/' + this.intezmeny); }
    catch { this.naploUgyfelek = []; }
    this.importModal.present();
  }
  async importMent() {
    const idk = Object.keys(this.importValasztott).filter((k) => this.importValasztott[k]);
    if (!idk.length) return this.hiba('Nincs kijelölt ügyfél.');
    try {
      const r: any = await this.ber.post('/ugyfel/import-naplo', { intezmeny: this.intezmeny, naplo_ugyfel_idk: idk });
      await this.importModal.dismiss();
      await this.betolt();
      this.ok(`${r?.letrehozott || 0} ügyfél importálva.`);
    } catch (e: any) { this.hiba(e?.error?.error || 'Az import nem sikerült.'); }
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1600, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'danger' })).present(); }
}
